/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\pages\Admin\MonitoringFallbacks.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

/**
SELLERIE MARINE - MONITORING DES FALLBACKS ADMIN

📋 DESCRIPTION:
Interface d'administration pour le monitoring des fallbacks critiques.
Dashboard complet avec visualisation des alertes et statistiques.

🎯 RESPONSABILITÉS:
- Affichage du tableau de bord des fallbacks
- Visualisation des alertes récentes avec filtres
- Statistiques détaillées par type et sévérité
- État de santé du système en temps réel
- Export des données vers CSV/Excel

🔗 DÉPENDANCES:
- FallbackService: API pour les données de monitoring
- React: Hooks useState, useEffect pour la gestion d'état
- Tailwind CSS: Styles et composants UI

📡 INTERACTIONS:
- Backend: Routes /api/fallback/*
- FallbackService: Interface de communication
- Notifications: Feedback utilisateur
- Auto-refresh: Mise à jour automatique

📊 STATUT: ACTIF (Production)
📅 Dernière mise à jour: Juillet 2025
👤 Responsable: Équipe développement

💡 NOTES:
- Interface responsive avec Tailwind CSS
- Auto-refresh toutes les 30 secondes
- Codes couleur selon la sévérité des alertes
- Export des données en temps réel
*/

import React, { useState, useEffect } from 'react';
import FallbackNotificationService, { FallbackAlert as LocalFallbackAlert } from '../../services/FallbackNotificationService';
import { apiFetch } from '../../services/httpClient';

// Types pour les données de fallback
interface FallbackAlert {
  timestamp: string;
  type: string;
  severity: string;
  context: string;
  fallback_value: string;
  expected_source: string;
  user_id?: string;
  article_id?: string;
  devis_id?: string;
  error_message?: string;
}

interface FallbackStatistics {
  total: number;
  recent_24h: number;
  by_type: Record<string, number>;
  by_severity: Record<string, number>;
}

interface FallbackHealth {
  status: 'healthy' | 'warning' | 'critical' | 'error';
  message: string;
  metrics: {
    total_fallbacks: number;
    recent_24h: number;
    critical_count: number;
  };
}

interface FallbackDashboard {
  summary: {
    total_fallbacks: number;
    recent_24h: number;
    critical_count: number;
    high_count: number;
  };
  by_type: Record<string, number>;
  by_severity: Record<string, number>;
  recent_alerts: FallbackAlert[];
  critical_alerts: FallbackAlert[];
}

const MonitoringFallbacks: React.FC = () => {
  // États pour les données
  const [dashboard, setDashboard] = useState<FallbackDashboard | null>(null);
  const [alerts, setAlerts] = useState<FallbackAlert[]>([]);
  const [statistics, setStatistics] = useState<FallbackStatistics | null>(null);
  const [health, setHealth] = useState<FallbackHealth | null>(null);
  
  // États pour l'interface
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  
  // États pour les fallbacks locaux
  const [localAlerts, setLocalAlerts] = useState<LocalFallbackAlert[]>([]);
  const [backendAvailable, setBackendAvailable] = useState(true);
  
  // États pour les filtres
  const [filterType, setFilterType] = useState<string>('all');
  const [filterSeverity, setFilterSeverity] = useState<string>('all');
  const [alertsLimit, setAlertsLimit] = useState<number>(50);

  // Fonction pour appeler l'API
  const fetchData = async (endpoint: string) => {
    return apiFetch(`/fallback/${endpoint}`);
  };

  // Charger le dashboard
  const loadDashboard = async () => {
    try {
      const data = await fetchData('dashboard');
      if (data.success) {
        setDashboard(data.dashboard);
      }
    } catch (err) {
      console.error('Erreur chargement dashboard:', err);
    }
  };

  // Charger les alertes
  const loadAlerts = async () => {
    try {
      const data = await fetchData(`alerts?limit=${alertsLimit}`);
      if (data.success) {
        setAlerts(data.alerts);
      }
    } catch (err) {
      console.error('Erreur chargement alertes:', err);
    }
  };

  // Charger les statistiques
  const loadStatistics = async () => {
    try {
      const data = await fetchData('statistics');
      if (data.success) {
        setStatistics(data.statistics);
      }
    } catch (err) {
      console.error('Erreur chargement statistiques:', err);
    }
  };

  // Charger l'état de santé
  const loadHealth = async () => {
    try {
      const data = await fetchData('health');
      if (data.success) {
        setHealth(data.health);
      }
    } catch (err) {
      console.error('Erreur chargement santé:', err);
    }
  };

  // Charger les fallbacks locaux
  const loadLocalFallbacks = () => {

    const localFallbackAlerts = FallbackNotificationService.getActiveAlerts();

    setLocalAlerts(localFallbackAlerts);
    
    // Convertir les alertes locales au format attendu par l'interface
    const convertedAlerts: FallbackAlert[] = localFallbackAlerts.map(alert => ({
      timestamp: alert.timestamp.toISOString(),
      type: alert.type,
      severity: alert.severity,
      context: alert.context || 'Fallback local',
      fallback_value: alert.fallbackValue || 'Valeur par défaut',
      expected_source: 'Backend API',
      user_id: 'local'
    }));
    
    setAlerts(convertedAlerts);
    
    // Créer un dashboard basé sur les alertes locales
    if (convertedAlerts.length > 0) {
      const byType: Record<string, number> = {};
      const bySeverity: Record<string, number> = {};
      
      convertedAlerts.forEach(alert => {
        byType[alert.type] = (byType[alert.type] || 0) + 1;
        bySeverity[alert.severity] = (bySeverity[alert.severity] || 0) + 1;
      });
      
      const criticalCount = bySeverity['critical'] || 0;
      const highCount = bySeverity['high'] || 0;
      
      setDashboard({
        summary: {
          total_fallbacks: convertedAlerts.length,
          recent_24h: convertedAlerts.length,
          critical_count: criticalCount,
          high_count: highCount
        },
        by_type: byType,
        by_severity: bySeverity,
        recent_alerts: convertedAlerts,
        critical_alerts: convertedAlerts.filter(a => a.severity === 'critical')
      });
      
      // Définir l'état de santé basé sur les alertes locales
      const status = criticalCount > 0 ? 'critical' : highCount > 0 ? 'warning' : 'healthy';
      setHealth({
        status,
        message: `${convertedAlerts.length} fallback(s) actif(s) détecté(s) localement (backend indisponible)`,
        metrics: {
          total_fallbacks: convertedAlerts.length,
          recent_24h: convertedAlerts.length,
          critical_count: criticalCount
        }
      });
    } else {
      // Aucun fallback local
      setDashboard({
        summary: { total_fallbacks: 0, recent_24h: 0, critical_count: 0, high_count: 0 },
        by_type: {},
        by_severity: {},
        recent_alerts: [],
        critical_alerts: []
      });
      
      setHealth({
        status: 'error',
        message: 'Backend indisponible - Mode local actif',
        metrics: { total_fallbacks: 0, recent_24h: 0, critical_count: 0 }
      });
    }
  };

  // Charger toutes les données
  const loadAllData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Essayer de charger depuis le backend
      await Promise.all([
        loadDashboard(),
        loadAlerts(),
        loadStatistics(),
        loadHealth()
      ]);
      
      setBackendAvailable(true);
      setLastUpdate(new Date());
    } catch (err) {

      setBackendAvailable(false);
      setError('Backend indisponible - Affichage des fallbacks locaux');
      
      // Charger les fallbacks locaux à la place
      loadLocalFallbacks();
      setLastUpdate(new Date());
    } finally {
      setLoading(false);
    }
  };

  // Charger les données au montage
  useEffect(() => {
    loadAllData();
  }, [alertsLimit]);

  // S'abonner aux alertes locales pour mise à jour en temps réel
  useEffect(() => {
    let isUpdating = false; // Protection contre les boucles infinies
    
    const unsubscribe = FallbackNotificationService.subscribeToAlerts((alerts) => {

      if (!backendAvailable && !isUpdating) {
        isUpdating = true;
        // Si le backend n'est pas disponible, mettre à jour avec les alertes locales
        setTimeout(() => {
          loadLocalFallbacks();
          isUpdating = false;
        }, 100); // Petit délai pour éviter les boucles
      }
    });

    return unsubscribe;
  }, [backendAvailable]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadAllData();
    }, 30000); // 30 secondes

    return () => clearInterval(interval);
  }, [autoRefresh, alertsLimit]);

  // Fonction pour obtenir la couleur selon la sévérité
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Fonction pour obtenir l'icône selon la sévérité
  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return '💥';
      case 'high':
        return '🔥';
      case 'medium':
        return '🚨';
      case 'low':
        return '⚠️';
      default:
        return '❓';
    }
  };

  // Fonction pour obtenir la couleur de l'état de santé
  const getHealthColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'error':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Filtrer les alertes
  const filteredAlerts = alerts.filter(alert => {
    const matchesType = filterType === 'all' || alert.type === filterType;
    const matchesSeverity = filterSeverity === 'all' || alert.severity === filterSeverity;
    return matchesType && matchesSeverity;
  });

  // Fonction pour formater la date
  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('fr-FR');
  };

  // Fonction pour exporter en CSV
  const exportToCSV = () => {
    const headers = ['Timestamp', 'Type', 'Sévérité', 'Contexte', 'Valeur Fallback', 'Source Attendue', 'Utilisateur', 'Erreur'];
    const csvContent = [
      headers.join(','),
      ...filteredAlerts.map(alert => [
        alert.timestamp,
        alert.type,
        alert.severity,
        `"${alert.context}"`,
        alert.fallback_value,
        alert.expected_source,
        alert.user_id || '',
        `"${alert.error_message || ''}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `fallback_alerts_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Fonction pour remettre à zéro les alertes
  const resetAlerts = async () => {
    if (!window.confirm("Etes-vous sur de vouloir remettre a zero toutes les donnees d'alertes ? Cette action est irreversible.")) {
      return;
    }

    try {
      setLoading(true);
      const data = await apiFetch('/fallback/reset', {
        method: 'POST'
      });

      if (data.success) {
        // Émettre un événement global pour notifier tous les composants
        window.dispatchEvent(new CustomEvent('fallbacks-reset'));
        
        // Recharger toutes les données après la remise à zéro
        await loadAllData();
        alert('✅ Toutes les données d\'alertes ont été remises à zéro avec succès !');
      } else {
        throw new Error(data.error || 'Erreur lors de la remise à zéro');
      }
    } catch (err) {
      console.error('Erreur remise à zéro:', err);
      alert('❌ Erreur lors de la remise à zéro: ' + (err instanceof Error ? err.message : 'Erreur inconnue'));
    } finally {
      setLoading(false);
    }
  };

  if (loading && !dashboard) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Chargement du monitoring...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* En-tête */}
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            📊 Monitoring des Fallbacks
          </h1>
          <p className="text-gray-600">
            Surveillance en temps réel des fallbacks critiques du système.
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Auto-refresh toggle */}
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm text-gray-600">Auto-refresh (30s)</span>
          </label>
          
          {/* Dernière mise à jour */}
          <span className="text-xs text-gray-500">
            Dernière MAJ: {lastUpdate.toLocaleTimeString('fr-FR')}
          </span>
          
          {/* Bouton refresh manuel */}
          <button
            onClick={loadAllData}
            disabled={loading}
            className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            🔄 Actualiser
          </button>
        </div>
      </div>

      {/* Messages d'erreur */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex">
            <span className="text-red-400 mr-3">❌</span>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* État de santé */}
      {health && (
        <div className={`mb-6 p-4 border rounded-lg ${getHealthColor(health.status)}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-2xl mr-3">
                {health.status === 'healthy' ? '✅' : 
                 health.status === 'warning' ? '⚠️' : 
                 health.status === 'critical' ? '🚨' : '❌'}
              </span>
              <div>
                <h3 className="font-semibold">État du système: {health.status.toUpperCase()}</h3>
                <p className="text-sm">{health.message}</p>
              </div>
            </div>
            <div className="text-right text-sm">
              <div>Total: {health.metrics.total_fallbacks}</div>
              <div>24h: {health.metrics.recent_24h}</div>
              <div>Critiques: {health.metrics.critical_count}</div>
            </div>
          </div>
        </div>
      )}

      {/* Résumé du dashboard */}
      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <span className="text-2xl mr-3">📊</span>
              <div>
                <p className="text-sm text-gray-600">Total Fallbacks</p>
                <p className="text-2xl font-bold text-gray-900">{dashboard.summary.total_fallbacks}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <span className="text-2xl mr-3">🕐</span>
              <div>
                <p className="text-sm text-gray-600">Dernières 24h</p>
                <p className="text-2xl font-bold text-blue-600">{dashboard.summary.recent_24h}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <span className="text-2xl mr-3">💥</span>
              <div>
                <p className="text-sm text-gray-600">Critiques</p>
                <p className="text-2xl font-bold text-red-600">{dashboard.summary.critical_count}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <span className="text-2xl mr-3">🔥</span>
              <div>
                <p className="text-sm text-gray-600">Élevées</p>
                <p className="text-2xl font-bold text-orange-600">{dashboard.summary.high_count}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Statistiques par type et sévérité */}
      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Par type */}
          <div className="bg-white p-4 rounded-lg shadow border">
            <h3 className="text-lg font-semibold mb-4">📈 Répartition par Type</h3>
            <div className="space-y-2">
              {Object.entries(dashboard.by_type).map(([type, count]) => (
                <div key={type} className="flex justify-between items-center">
                  <span className="capitalize">{type}</span>
                  <span className="font-semibold">{count}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Par sévérité */}
          <div className="bg-white p-4 rounded-lg shadow border">
            <h3 className="text-lg font-semibold mb-4">🎯 Répartition par Sévérité</h3>
            <div className="space-y-2">
              {Object.entries(dashboard.by_severity).map(([severity, count]) => (
                <div key={severity} className="flex justify-between items-center">
                  <div className="flex items-center">
                    <span className="mr-2">{getSeverityIcon(severity)}</span>
                    <span className="capitalize">{severity}</span>
                  </div>
                  <span className="font-semibold">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Contrôles des alertes */}
      <div className="mb-4 flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Filtre par type */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Tous les types</option>
            <option value="fermetures">Fermetures</option>
            <option value="mousses">Mousses</option>
            <option value="tva">TVA</option>
            <option value="tarifs">Tarifs</option>
            <option value="optimisation">Optimisation</option>
            <option value="matieres">Matières</option>
          </select>

          {/* Filtre par sévérité */}
          <select
            value={filterSeverity}
            onChange={(e) => setFilterSeverity(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Toutes les sévérités</option>
            <option value="critical">Critique</option>
            <option value="high">Élevée</option>
            <option value="medium">Moyenne</option>
            <option value="low">Faible</option>
          </select>

          {/* Limite d'alertes */}
          <select
            value={alertsLimit}
            onChange={(e) => setAlertsLimit(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          >
            <option value={20}>20 alertes</option>
            <option value={50}>50 alertes</option>
            <option value={100}>100 alertes</option>
            <option value={200}>200 alertes</option>
          </select>
        </div>

        {/* Boutons d'action */}
        <div className="flex gap-2">
          <button
            onClick={exportToCSV}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:ring-2 focus:ring-green-500"
          >
            📥 Exporter CSV
          </button>
          
          <button
            onClick={resetAlerts}
            disabled={loading}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:ring-2 focus:ring-red-500 disabled:opacity-50"
          >
            🗑️ Remettre à zéro
          </button>
        </div>
      </div>

      {/* Tableau des alertes */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            🚨 Alertes Récentes ({filteredAlerts.length})
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sévérité
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contexte
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fallback
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Utilisateur
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAlerts.map((alert, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(alert.timestamp)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {alert.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getSeverityColor(alert.severity)}`}>
                      {getSeverityIcon(alert.severity)} {alert.severity}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate" title={alert.context}>
                    {alert.context}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                      {alert.fallback_value}
                    </code>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {alert.user_id || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredAlerts.length === 0 && (
            <div className="text-center py-12">
              <span className="text-4xl mb-4 block">✅</span>
              <p className="text-gray-500">Aucune alerte trouvée</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MonitoringFallbacks;
